import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import LightIcon from '@material-ui/icons/WbSunny';
import { UnifiedThemeProvider } from '@backstage/theme';
import { myTheme } from './themes/myTheme';
import { AvaliaDxHubBetsDemoPage, UsersManagerPage, HomePage, PersonalSatisfactionTracker, TeamsManagerPage, SatisfactionTrackerPage, TeamSatisfactionDashboard } from '@internal/plugin-satisfaction-tracker';
import { AvaliaDxHubBetRouter } from '@avaliasystems/backstage-plugin-avalia-dxhub-bets';
import Box from '@material-ui/core/Box';
import { googleAuthApiRef } from '@backstage/core-plugin-api';
import { Navigate } from 'react-router-dom';

const isLocalDevEnvironment = process.env.NODE_ENV === 'development';

const app = createApp({
  apis,
  themes: [
    {
      id: 'my-theme',
      title: 'Main Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={myTheme} children={children} />
      ),
    },
  ],

  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={
          isLocalDevEnvironment
            ? [
                'guest',
                {
                  id: 'google-auth-provider',
                  title: 'Google',
                  message: 'Sign in using Google',
                  apiRef: googleAuthApiRef,
                },
              ]
            : [
                {
                  id: 'google-auth-provider',
                  title: 'Google',
                  message: 'Sign in using Google',
                  apiRef: googleAuthApiRef,
                },
              ]
        }
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="home-page" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route
      path="/avalia-dxhub-bets-demo"
      element={<AvaliaDxHubBetsDemoPage />}
    />
    <Route
      path="avalia-dxhub-bets"
      element={
        <Box paddingLeft={2}>
          <AvaliaDxHubBetRouter />
        </Box>
      }
    />
    <Route path="users-manager" element={<UsersManagerPage />} />
    <Route path="teams-manager" element={<TeamsManagerPage/>}/>
    <Route path="dev-satisfaction-tracker" element={<SatisfactionTrackerPage/>}/>
    <Route path='teams-satisfaction' element={<TeamSatisfactionDashboard/>}/>
    <Route path="personal-satisfaction-tracker" element={<PersonalSatisfactionTracker/>}/>
    <Route path="home-page" element={<HomePage/>}/>

  

   
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
